import { useEffect } from "react";

import type { JourneyMode } from "@/data/schema/compiled";
import {
  activeBeat,
  type BookTimelineLayout,
} from "@/lib/timeline/book/BookLayout";
import type { PlaybackState } from "@/lib/timeline/playback";
import {
  playheadForMoment,
  storyMomentAt,
  type StoryMoment,
} from "./universeJourneyLoad";

interface JourneySwitchInput {
  journey: JourneyMode;
  layout: BookTimelineLayout;
  requestJourney: (next: JourneyMode, moment: StoryMoment | undefined) => void;
  keepMoment: (moment: StoryMoment | undefined) => void;
  takePendingMoment: () => { moment: StoryMoment | undefined } | undefined;
}

interface JourneySwitchPlayback {
  pause: () => void;
  seekTo: (playheadMs: number) => void;
  getPlayback: () => PlaybackState;
}

/**
 * Playhead milliseconds differ per journey and per chapter selection, so a change
 * carries the story moment, and starts over if that scene is no longer on the rail.
 */
export function useJourneySwitch(
  view: JourneySwitchInput,
  playback: JourneySwitchPlayback,
  setSpanId: (id: string) => void,
  resetCamera: (() => void) | undefined,
): {
  onJourneyChange: (next: JourneyMode) => void;
  onSpanIdChange: (id: string) => void;
} {
  const { journey, layout } = view;

  // Passive, so the session flush for the old rail saves its own playhead first.
  useEffect(() => {
    const pending = view.takePendingMoment();
    if (pending === undefined) {
      return;
    }
    const playheadMs = pending.moment && playheadForMoment(layout.beats, pending.moment);
    playback.seekTo(playheadMs ?? 0);
  });

  const currentMoment = (): StoryMoment | undefined => {
    const playheadMs = playback.getPlayback().playheadMs;
    return storyMomentAt(activeBeat(layout, playheadMs), playheadMs);
  };

  return {
    onJourneyChange: (next) => {
      if (next !== journey) {
        playback.pause();
      }
      view.requestJourney(next, currentMoment());
    },
    onSpanIdChange: (id) => {
      view.keepMoment(currentMoment());
      setSpanId(id);
      resetCamera?.();
    },
  };
}
